import { createContext, useContext, useEffect, useMemo, useState } from "react";

const LanguageContext = createContext(null);

const STORAGE_KEY = "premium-tour-miyako-language";

export const LANGS = ["中文", "한국어", "EN", "JP"];

export const LANGUAGE_LABELS = {
  中文: "中文",
  한국어: "한국어",
  EN: "EN",
  JP: "JP",
};

const DEFAULT_LANG = "JP";
const URL_TO_LANG = { en: "EN", zh: "中文", ko: "한국어" };
const LANG_TO_URL = { EN: "en", 中文: "zh", 한국어: "ko" };

export const getUrlPrefixForLang = (lang) => LANG_TO_URL[lang] || "";

export const stripLocalePrefix = (pathname = "/") => {
  const cleanPath = pathname || "/";

  return cleanPath.replace(/^\/(?:en|zh|ko)(?=\/|$)/, "") || "/";
};

export const getLocalizedPath = (pathname = "/", lang = DEFAULT_LANG) => {
  const pathWithoutLocale = stripLocalePrefix(pathname);
  const prefix = getUrlPrefixForLang(lang);

  if (!prefix) return pathWithoutLocale;
  if (pathWithoutLocale === "/") return `/${prefix}`;

  return `/${prefix}${pathWithoutLocale}`;
};

const isValidLang = (value) => {
  return LANGS.includes(value);
};

const getInitialLang = () => {
  if (typeof window === "undefined") return DEFAULT_LANG;

  const pathLang = window.location.pathname.split("/")[1];
  if (URL_TO_LANG[pathLang]) return URL_TO_LANG[pathLang];

  const savedLang = window.localStorage.getItem(STORAGE_KEY);

  return isValidLang(savedLang) ? savedLang : DEFAULT_LANG;
};

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => getInitialLang());

  const setLang = (nextLang) => {
    if (!isValidLang(nextLang)) return;

    setLangState(nextLang);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, nextLang);

      const nextPath = getLocalizedPath(window.location.pathname, nextLang);
      window.history.replaceState(
        window.history.state,
        "",
        `${nextPath}${window.location.search}${window.location.hash}`,
      );
    }
  };

  useEffect(() => {
    if (typeof document === "undefined") return;

    const htmlLangMap = {
      中文: "zh",
      한국어: "ko",
      EN: "en",
      JP: "ja",
    };

    document.documentElement.lang = htmlLangMap[lang] || "ja";
  }, [lang]);

  const value = useMemo(() => {
    return {
      lang,
      setLang,
      langs: LANGS,
      languageLabels: LANGUAGE_LABELS,
    };
  }, [lang]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }

  return context;
};
